/**
 * One-time migration: moves inline base64 photo data from
 * localStorage (via completions) into IndexedDB.
 * Runs on app startup; is a no-op once all photos are migrated.
 */
import { useProgressStore } from '../store/progressStore';
import { savePhoto, IDB_PREFIX } from './photo-db';

export async function migratePhotosToIndexedDB(): Promise<void> {
  const { completions } = useProgressStore.getState();

  // Find completions with inline base64 photos (not already migrated)
  const toMigrate = completions.filter(
    c => c.photoUrl && !c.photoUrl.startsWith(IDB_PREFIX)
  );

  if (toMigrate.length === 0) return;

  // Save each photo to IndexedDB and collect updated records
  const updates: { id: string; newUrl: string }[] = [];

  for (const c of toMigrate) {
    try {
      const idbUrl = await savePhoto(c.id, c.photoUrl!);
      updates.push({ id: c.id, newUrl: idbUrl });
    } catch {
      // If a single photo fails, skip it — it'll retry next launch
      console.warn(`Failed to migrate photo for completion ${c.id}`);
    }
  }

  if (updates.length === 0) return;

  // Batch-update all completions in the store
  const updateMap = new Map(updates.map(u => [u.id, u.newUrl]));
  useProgressStore.setState(s => ({
    completions: s.completions.map(c =>
      updateMap.has(c.id) ? { ...c, photoUrl: updateMap.get(c.id) } : c
    ),
  }));

  console.log(`Migrated ${updates.length} photo(s) to IndexedDB`);
}
